import { visionBoardApi } from './api/visionBoard';
import { auth } from '../config/firebase';
import { collection, query, where, getDocs, addDoc, serverTimestamp, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

class VisionBoardService {
  constructor(api) {
    this.api = api;
  }

  checkAuth() {
    if (!auth.currentUser) {
      throw new Error('No autorizado');
    }
  }

  handleError(error, operation) {
    console.error(`Error ${operation}:`, error);
    if (error.code === 'permission-denied') {
      throw new Error('No tienes permisos para realizar esta operación');
    }
    throw error;
  }

  async getCards() {
    try {
      return await this.api.getCards();
    } catch (error) {
      this.handleError(error, 'fetching cards');
    }
  }

  async updateCard(id, updates) {
    try {
      this.checkAuth();
      return await this.api.updateCard(id, updates);
    } catch (error) {
      console.error('Error updating card:', error);
      throw error;
    }
  }

  async deleteCard(cardId) {
    try {
      this.checkAuth();
      if (!cardId) {
        throw new Error('ID de tarjeta inválido');
      }
      const cardRef = doc(db, 'cards', cardId);
      const cardDoc = await getDoc(cardRef);
      
      if (!cardDoc.exists()) {
        throw new Error('La tarjeta no existe');
      }
      
      const cardData = cardDoc.data();
      if (cardData.userId !== auth.currentUser.uid) {
        throw new Error('No tienes permiso para eliminar esta tarjeta');
      }
      
      await deleteDoc(cardRef);
      return true;
    } catch (error) {
      console.error('Error deleting card:', error);
      throw error;
    }
  }

  async getUserByUsername(username) {
    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('username', '==', username));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log('No se encontró usuario con username:', username);
        throw new Error('Usuario no encontrado');
      }
      
      const userDoc = snapshot.docs[0];
      return {
        uid: userDoc.id,
        ...userDoc.data()
      };
    } catch (error) {
      console.error('Error getting user:', error);
      throw error;
    }
  }

  async getUserBoard(userId, isOwner = false) {
    try {
      const cardsRef = collection(db, 'cards');
      let q = query(cardsRef, where('userId', '==', userId));
      
      if (!isOwner) {
        q = query(cardsRef, where('userId', '==', userId), where('isPublic', '==', true));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        docId: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting user board:', error);
      throw error;
    }
  }

  async getPublicBoards() {
    try {
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const users = usersSnapshot.docs.map(doc => ({
        userId: doc.id,
        ...doc.data()
      }));

      const publicBoards = await Promise.all(
        users.map(async (user) => {
          const cardsRef = collection(db, 'cards');
          const q = query(
            cardsRef,
            where('userId', '==', user.userId),
            where('isPublic', '==', true)
          );
          const cardsSnapshot = await getDocs(q);
          const cardCount = cardsSnapshot.size;

          if (cardCount > 0) {
            return {
              ...user,
              cardCount
            };
          }
          return null;
        })
      );

      return publicBoards.filter(board => board !== null);
    } catch (error) {
      console.error('Error getting public boards:', error);
      throw error;
    }
  }

  async addCard(cardData) {
    try {
      this.checkAuth();
      const cardsRef = collection(db, 'cards');
      const newCard = {
        ...cardData,
        userId: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        isPublic: cardData.isPublic ?? true
      };
      
      const docRef = await addDoc(cardsRef, newCard);
      return { docId: docRef.id, ...newCard };
    } catch (error) {
      console.error('Error adding card:', error);
      throw error;
    }
  }
}

// Creamos una instancia única del servicio
export const visionBoardService = new VisionBoardService(visionBoardApi); 